export const COLORS = {
  PRIMARY: "#154A8A",
  PRIMARY_DARK: "#010953",
  PRIMARY_LIGHT: "#0075AD",
  SECONDARY: "#0091D6",
  ERROR: "#AE2633",
  ERROR_DARK: "#C82333",
  ERROR_LIGHT: "#E97B86",
  WARNING: "#FFC107",
  INFO: "#6C757D",
  SUCCESS: "#28A745",
  PRIMARY_TEXT: "#000000",
  SECONDARY_TEXT: "#707173",
  NEUTRAL: "#E0E0E0",
  NEUTRAL_LIGHT: "#EAECEF",
  WHITE: "#fff",
  BACKGROUND_COLOR: "#F2F7F8",
  DISABLED: "#C0C0C0",
  GRAY_ACTION: "#939393",
  REQUIRED: "#DB3131",
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    p: 2,
  },
  modalBackground: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "920px",
    maxWidth: "calc(100% - 32px)",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: COLORS.WHITE,
    borderRadius: "5px",
    boxShadow: "0 0 24px -4px rgba(0, 0, 0, 0.35)",
    outline: "none",
    overflow: "hidden",
  },
  headingContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    p: "24px 21px 10px",
  },
  headingTitle: {
    color: "#1B1E21",
    fontSize: 18,
    fontWeight: 600,
    m: 0,
  },
  headingSubtitle: {
    color: COLORS.SECONDARY_TEXT,
    fontSize: 13,
    mt: "2px",
    mb: 0,
  },
  closeButton: {
    minWidth: 0,
    p: 0,
    color: COLORS.GRAY_ACTION,
  },
  divider: {
    borderBottom: `1px solid ${COLORS.NEUTRAL_LIGHT}`,
    opacity: 0.8,
  },
  body: {
    p: "18px 21px",
    maxHeight: "62vh",
    overflowY: "auto",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: "14px 21px",
  },
  footerNote: {
    fontSize: 13,
    color: COLORS.SECONDARY_TEXT,
  },
  buttonRow: {
    display: "flex",
    gap: 1,
  },
  cancelButton: {
    minWidth: 82,
    height: 31,
    px: "14px",
    color: COLORS.PRIMARY,
    bgcolor: COLORS.WHITE,
    border: `1px solid ${COLORS.PRIMARY_DARK}`,
    borderRadius: "4px",
    fontSize: 13,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": { bgcolor: COLORS.WHITE },
  },
  primaryButton: (disabled: boolean) => ({
    minWidth: 82,
    height: 31,
    px: "14px",
    color: COLORS.WHITE,
    bgcolor: disabled ? "#99BFED" : COLORS.PRIMARY,
    borderRadius: "4px",
    fontSize: 13,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": { bgcolor: disabled ? "#99BFED" : COLORS.PRIMARY_DARK },
  }),
  fieldLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.PRIMARY_TEXT,
    mb: "4px",
  },
  required: { color: COLORS.REQUIRED },
  select: {
    width: "100%",
    height: 36.6,
    fontSize: 14,
    color: COLORS.PRIMARY_TEXT,
    bgcolor: COLORS.WHITE,
    borderRadius: "4px",
    "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
  },
  selectDisabled: {
    width: "100%",
    height: 36.6,
    fontSize: 14,
    color: COLORS.SECONDARY_TEXT,
    bgcolor: "#F7F7F7",
    borderRadius: "4px",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.PRIMARY_DARK,
  },
  linkButton: {
    minWidth: 0,
    p: 0,
    color: COLORS.PRIMARY_LIGHT,
    fontSize: 13,
    fontWeight: 500,
    textTransform: "none",
  },
  tableContainer: {
    border: `1px solid ${COLORS.NEUTRAL}`,
    borderRadius: "4px",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    bgcolor: COLORS.NEUTRAL_LIGHT,
    color: COLORS.SECONDARY_TEXT,
    fontWeight: 600,
    p: "8px 12px",
    borderBottom: `1px solid ${COLORS.NEUTRAL}`,
  },
  td: {
    p: "10px 12px",
    borderBottom: `1px solid ${COLORS.NEUTRAL_LIGHT}`,
    verticalAlign: "top",
    color: COLORS.PRIMARY_TEXT,
  },
  chip: (bg: string, fg: string) => ({
    display: "inline-block",
    px: "10px",
    py: "3px",
    borderRadius: "12px",
    fontSize: 12,
    fontWeight: 600,
    bgcolor: bg,
    color: fg,
  }),
  warningBox: {
    mt: "14px",
    p: "12px 14px",
    borderRadius: "4px",
    border: `1px solid ${COLORS.WARNING}`,
    bgcolor: "#FFF8E1",
  },
  errorBox: {
    p: "12px 14px",
    borderRadius: "4px",
    border: `1px solid ${COLORS.ERROR_LIGHT}`,
    bgcolor: "#FBEAEC",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    fontSize: 13,
    color: COLORS.PRIMARY_TEXT,
    mt: "6px",
    cursor: "pointer",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px",
    bgcolor: COLORS.WHITE,
    borderRadius: "4px 4px 0 0",
    border: `1px solid ${COLORS.NEUTRAL}`,
    p: "10px 16px",
  },
  toolbarButton: {
    height: 31,
    px: "14px",
    fontSize: 13,
    fontWeight: 500,
    bgcolor: "#CFDAE7",
    color: COLORS.PRIMARY_DARK,
    borderRadius: "4px",
    textTransform: "none",
  },
  copyButton: {
    textTransform: 'capitalize',
    height: '34px',
    width: '120px',
    minWidth: 'fit-content',
    margin: '0.5rem',
    padding: '1rem',
    ':hover': {
      backgroundColor: '#0075AD',
      color: '#ffffff',
    }
  },
  toolbarMeta: {
    ml: "auto",
    display: "flex",
    alignItems: "center",
    gap: 1,
    fontSize: 13,
    color: COLORS.SECONDARY_TEXT,
  },
  pageContent: {
    display: "grid",
    gridTemplateColumns: "300px 1fr",
    gap: 2,
    bgcolor: COLORS.WHITE,
    border: `1px solid ${COLORS.NEUTRAL}`,
    borderTop: "none",
    borderRadius: "0 0 4px 4px",
    p: 2,
  },
  inactiveLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: 12,
    color: COLORS.SECONDARY_TEXT,
  },
  listRow: (selected: boolean, justCopied: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    p: "8px 10px",
    borderRadius: "4px",
    fontSize: 13,
    bgcolor: selected ? COLORS.PRIMARY : justCopied ? "#E6F4EA" : "transparent",
    color: selected ? COLORS.WHITE : COLORS.PRIMARY_TEXT,
  }),
  contextPanel: {
    border: `1px dashed ${COLORS.NEUTRAL}`,
    borderRadius: "4px",
    p: 2,
  },
  criteriaBar: {
    mt: 1.5,
    display: "flex",
    alignItems: "center",
    gap: 1,
    p: "8px 12px",
    bgcolor: COLORS.NEUTRAL_LIGHT,
    borderRadius: "4px",
    fontSize: 13,
    color: COLORS.SECONDARY_TEXT,
  },
  toast: {
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 60,
    bgcolor: "#EDF7ED",
    border: `1px solid ${COLORS.SUCCESS}`,
    borderRadius: "4px",
    p: "10px 16px",
    minWidth: 280,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
  },
  page: {
    minHeight: "100vh",
    bgcolor: COLORS.BACKGROUND_COLOR,
    fontFamily: "Roboto, Arial, sans-serif",
    color: COLORS.PRIMARY_TEXT,
  },
  pageContainer: {
    maxWidth: 1080,
    mx: "auto",
    p: 3,
  },
  collateralRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "12px",
  },

  collateralField: {
    flex: 1,
  },

  arrowIcon: {
    color: COLORS.GRAY_ACTION,
    marginBottom: "8px",
  },

  autocomplete: {
    "& .MuiOutlinedInput-root": {
      minHeight: "36.6px",
      fontSize: "14px",
      borderRadius: "4px",
    },

    "& .MuiInputBase-input": {
      fontSize: "14px",
    },
  },

  resolutionRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  resolutionAutocomplete: {
    width: "190px",

    "& .MuiOutlinedInput-root": {
      minHeight: "31px",
      fontSize: "12px",
    },

    "& .MuiInputBase-input": {
      fontSize: "12px",
    },
  },

  resolutionAutocompleteMapped: {
    width: "190px",

    "& .MuiOutlinedInput-root": {
      minHeight: "31px",
      fontSize: "12px",
      backgroundColor: "#E6F4EA",

      "& fieldset": {
        borderColor: COLORS.SUCCESS,
      },
    },

    "& .MuiInputBase-input": {
      fontSize: "12px",
      color: COLORS.SUCCESS,
    },
  },

  removeRuleButton: {
    height: "31px",
    padding: "0 12px",
    fontSize: "12px",
    fontWeight: 500,
    color: COLORS.ERROR,
    borderColor: COLORS.ERROR_LIGHT,
    whiteSpace: "nowrap",
    textTransform: "none",
  },

  copyIcon: {
    fontSize: 25,
    color: '#154A8A',
    padding: '0',
    ':hover': {
      cursor: 'pointer',
    },
  },
};

export default styles;
